//client/src/components/plataforma/politicasETermos/PoliciesListPage.js
import React, { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePolicies } from '../../../hooks/usePolicies';
import useAlert from '../../../hooks/useAlert';
import { Plus, Edit, Trash2, Loader2, Gavel, MoreVertical } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';
import FormModal from '../../layout/FormModal';
import { policyTypeNames, audienceNames } from '../../../utils/translations';

const PolicyRow = ({ policy, onOpenActions }) => (
    <tr onClick={() => onOpenActions(policy)} style={{ cursor: 'pointer' }}>
        <td className="font-medium">{policy.title}</td>
        <td>{policyTypeNames[policy.type] || policy.type}</td>
        <td>{audienceNames[policy.audience] || policy.audience}</td>
        <td className="text-center">{policy.version}</td>
        <td>
            <span className={`badge ${policy.status === 'published' ? 'badge-success' : 'badge-warning'}`}>
                {policy.status === 'published' ? 'Publicado' : 'Rascunho'}
            </span>
        </td>
        <td className="text-right">
            <button
                onClick={(e) => { e.stopPropagation(); onOpenActions(policy); }}
                title="Ações"
                className="action-button-icon text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <MoreVertical size={18} />
            </button>
        </td>
    </tr>
);

const PoliciesListPage = () => {
    const navigate = useNavigate();
    const { policies, loading, error, deletePolicy } = usePolicies();
    const { setAlert } = useAlert();
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, policyId: null, policyTitle: '' });

    const openDeleteModal = (id, title) => {
        setDeleteModal({ isOpen: true, policyId: id, policyTitle: title });
        setSelectedPolicy(null);
    };

    const closeDeleteModal = () => {
        setDeleteModal({ isOpen: false, policyId: null, policyTitle: '' });
    };

    const confirmDelete = async () => {
        if (!deleteModal.policyId) return;
        try {
            await deletePolicy(deleteModal.policyId);
            setAlert('Documento excluído com sucesso!', 'success');
        } catch (err) {
            setAlert(`Erro ao excluir: ${err.message}`, 'error');
        } finally {
            closeDeleteModal();
        }
    };

    const actions = selectedPolicy ? [
        {
            key: 'editar',
            label: 'Editar Documento',
            icon: <Edit size={20} className="icon" />,
            onClick: () => { navigate(`/site-institucional/politicas/editar/${selectedPolicy._id}`); setSelectedPolicy(null); },
        },
        {
            key: 'excluir',
            label: 'Excluir Documento',
            icon: <Trash2 size={20} className="icon" />,
            onClick: () => openDeleteModal(selectedPolicy._id, selectedPolicy.title),
            destructive: true,
        },
    ] : [];

    if (error) {
        return <div className="alert alert-danger">Erro ao carregar documentos: {error}</div>;
    }

    const hasPolicies = policies.length > 0;

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Gavel />}
                    title="Termos e Políticas"
                    subtitle={`${policies.length} documento(s)`}
                />
            </div>

            <div className="table-container">
                <div className="table-wrapper">
                    {loading && policies.length === 0 ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin" />
                            <span>Carregando documentos...</span>
                        </div>
                    ) : hasPolicies ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Título</th>
                                    <th style={{ textAlign: 'left' }}>Tipo</th>
                                    <th style={{ textAlign: 'left' }}>Público</th>
                                    <th>Versão</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'right' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {policies.map((policy) => (
                                    <PolicyRow key={policy._id} policy={policy} onOpenActions={setSelectedPolicy} />
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <Gavel size={48} className="text-gray-400 mx-auto" />
                            <h3 className="text-lg font-semibold text-gray-700 mt-4 mb-2">Nenhum documento encontrado</h3>
                            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                                Cadastre o primeiro termo ou política da plataforma.
                            </p>
                            <button
                                onClick={() => navigate('/site-institucional/politicas/nova')}
                                className="action-button action-button-primary"
                            >
                                <Plus size={18} className="icon" /> Novo Documento
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ActionsDrawer
                open={!!selectedPolicy}
                onClose={() => setSelectedPolicy(null)}
                title={selectedPolicy?.title || 'Documento'}
                subtitle={selectedPolicy ? (policyTypeNames[selectedPolicy.type] || selectedPolicy.type) : null}
                actions={actions}
            />

            <FabButton
                onClick={() => navigate('/site-institucional/politicas/nova')}
                title="Novo Documento"
                visible={true}
                icon={<Gavel size={26} className="icon" />}
            />

            {deleteModal.isOpen && (
                <FormModal title="Confirmar Exclusão" onClose={closeDeleteModal} size="small">
                    <p>Você tem certeza que deseja excluir o documento "<strong>{deleteModal.policyTitle}</strong>"?</p>
                    <div className="form-actions">
                        <button type="button" onClick={closeDeleteModal} className="action-button action-button-secondary">Cancelar</button>
                        <button type="button" onClick={confirmDelete} className="action-button action-button-danger">Confirmar Exclusão</button>
                    </div>
                </FormModal>
            )}
        </Fragment>
    );
};

export default PoliciesListPage;
